import * as React from 'react';

import cx from 'classnames';

import { FocusIndicator } from '../FocusIndicator/FocusIndicator';
import { useButtonStyles } from './useButtonStyles';

type ToggleButtonProps = {
  children: React.ReactNode;
  id: string;
  onToggle: (expanded: boolean) => void;
  pressed: boolean;
  pressedLabel?: React.ReactNode;
};

/**
 * A button element that changes appearance whether in a pressed or
 * unpressed state.
 *
 * @see https://www.w3.org/TR/wai-aria-practices-1.1/#button
 * @see https://inclusive-components.design/toggle-button/
 */
export function ToggleButton({
  children,
  id,
  onToggle,
  pressed,
  pressedLabel = children,
}: ToggleButtonProps): React.ReactElement {
  const handleToggle = () => {
    const newValue = !pressed;

    if (typeof onToggle === 'function') {
      onToggle(newValue);
    }
  };

  const classNames = useButtonStyles({
    padding: 'standard',
    appearance: pressed ? 'primary' : 'default',
  });
  const buttonClass = cx(classNames, 'group');

  return (
    <button
      className={buttonClass}
      id={id}
      type="button"
      aria-pressed={pressed}
      onClick={handleToggle}
    >
      {pressed ? pressedLabel : children}
      <FocusIndicator />
    </button>
  );
}
